// src/pages/CropDetail.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Typography, Box, Paper, Button, Grid } from "@mui/material";
import apiClient from "../utils/apiClient";
import MonthBarChart from "../components/MonthBarChart";
import "../style/Crops.css";

const getMonthIndex = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getMonth();
};

const getGrowthTime = (crop) => {
  if (crop.harvest_min_days && crop.harvest_max_days) {
    if (crop.harvest_min_days !== crop.harvest_max_days) {
      return `${crop.harvest_min_days} - ${crop.harvest_max_days} days`;
    }
    return `${crop.harvest_min_days} days`;
  }
  if (crop.harvest_min_days) return `${crop.harvest_min_days} days`;
  if (crop.harvest_max_days) return `${crop.harvest_max_days} days`;
  return "N/A";
};

const isVegetableCrop = (crop) =>
  crop?.crop_type?.name &&
  crop.crop_type.name.toLowerCase().includes("vegetable");

const getCropIllustration = (crop) => {
  // Check if crop has a specific image
  if (crop.image_url) {
    return crop.image_url;
  }
  
  // Try to load crop-specific image first (lowercase crop name)
  if (crop.name) {
    const cropNameLower = crop.name.toLowerCase().replace(/\s+/g, ''); // Remove spaces and convert to lowercase
    return `/crop-illustrations/${cropNameLower}.png`;
  }
  
  // Fallback to placeholder based on crop type
  const type = crop.type?.toLowerCase();
  if (type?.includes('vegetable')) {
    return '/crop-illustrations/placeholders/vegetable-generic.svg';
  } else if (type?.includes('herb')) {
    return '/crop-illustrations/placeholders/herb-generic.svg';
  } else if (type?.includes('fruit')) {
    return '/crop-illustrations/placeholders/fruit-generic.svg';
  }
  
  // Default botanical illustration
  return '/crop-illustrations/placeholders/botanical-leaf.svg';
};

const CropDetail = () => {
  const { crop_uuid } = useParams();
  const [crop, setCrop] = useState(null);

  useEffect(() => {
    const fetchCrop = async () => {
      try {
        const response = await apiClient.get(`/crops/${crop_uuid}`);
        setCrop(response.data);
      } catch (err) {
        console.error("Failed to fetch crop:", err);
      }
    };
    fetchCrop();
  }, [crop_uuid]);

  if (!crop) return <Typography>Loading...</Typography>;

  // Build arrays of {start,end} per period_type (one interval per window)
  const getWindowIntervals = (periodType) => {
    const windows = (crop.crop_windows || []).filter((w) => w.period_type === periodType);
    return windows.map((w) => ({
      start: getMonthIndex(w.window_start),
      end: getMonthIndex(w.window_end),
    })).filter((i) => i.start !== null && i.end !== null);
  };

  const ranges = {
    preGermination: getWindowIntervals("pre_germination"),
    directSowing: getWindowIntervals("direct_sowing"),
    setting: getWindowIntervals("setting"),
    planting: getWindowIntervals("planting"),
    pruning: getWindowIntervals("pruning"),
    harvest: getWindowIntervals("harvest"),
    picking: getWindowIntervals("picking"),
  };

  return (
    <Box p={3}>
      <Button 
        component={Link} 
        to="/crops" 
        variant="outlined" 
        className="vintage-button"
        sx={{ mb: 2 }}
      >
        ⟸ Back to Crops
      </Button>

      {/* Title + Latin name */}
      <Typography 
        variant="h3" 
        gutterBottom 
        className="vintage-script-header"
      >
        {crop.name}
      </Typography>
      <Typography 
        variant="h5" 
        gutterBottom 
        sx={{ 
          fontStyle: 'italic', 
          textAlign: 'center', 
          color: 'var(--color-sepia)',
          fontFamily: 'var(--font-serif)',
          position: 'relative',
          mb: 4
        }}
      >
        {crop.latin_name}
        <span style={{ 
          position: 'absolute',
          bottom: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100px',
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, var(--color-accent-gold) 20%, var(--color-accent-gold) 80%, transparent 100%)'
        }}></span>
      </Typography>

      {/* Ornamental Divider */}
      <div className="section-divider"></div>

      {/* Botanical Details + Image */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            p: 3, 
            height: "100%",
            background: 'transparent', // Transparent to show parchment
            border: '2px solid var(--color-border-vintage)',
            borderRadius: 0, // Remove rounded edges
            boxShadow: 'none', // Remove shadows for clean look
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
            }}></div>
            <Typography variant="h6" gutterBottom className="vintage-text" sx={{ 
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              mt: 2
            }}>
              Botanical Details
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1, fontFamily: 'var(--font-serif)', color: 'var(--color-ink)' }}>
                <span className="gold-accent">Type:</span> {crop.crop_type?.name || 'Unknown'}
              </Typography>
              <Typography sx={{ mb: 1, fontFamily: 'var(--font-serif)', color: 'var(--color-ink)' }}>
                <span className="gold-accent">Soil Effect:</span> {crop.soil_effect}
              </Typography>
              <Typography sx={{ mb: 1, fontFamily: 'var(--font-serif)', color: 'var(--color-ink)' }}>
                <span className="gold-accent">Growth Time:</span> {getGrowthTime(crop)}
              </Typography>
              {isVegetableCrop(crop) && (
                <>
                  {crop.row_spacing_cm != null && (
                    <Typography sx={{ mb: 1, fontFamily: 'var(--font-serif)', color: 'var(--color-ink)' }}>
                      <span className="gold-accent">Row spacing:</span> {crop.row_spacing_cm} cm
                    </Typography>
                  )}
                  {crop.plant_spacing_cm != null && (
                    <Typography sx={{ fontFamily: 'var(--font-serif)', color: 'var(--color-ink)' }}>
                      <span className="gold-accent">Plant spacing:</span> {crop.plant_spacing_cm} cm
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ 
            textAlign: 'center',
            overflow: 'visible',
            position: 'relative'
          }}>
            <img 
              src={getCropIllustration(crop)} 
              alt={`${crop.name} botanical illustration`}
              style={{
                width: '300px',
                height: '300px',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                objectFit: 'cover',
                overflow: 'visible'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              style={{ 
                display: 'none',
                width: '300px',
                height: '300px',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '4rem',
                color: 'var(--color-sepia)',
                margin: '0 auto',
                overflow: 'visible'
              }}
            >
              🌿
            </div>
          </Box>
        </Grid>
      </Grid>

      {/* Description */}
      <Paper sx={{ 
        p: 3,
        background: 'transparent', // Transparent to show parchment
        border: '2px solid var(--color-border-vintage)',
        borderRadius: 0, // Remove rounded edges
        boxShadow: 'none', // Remove shadows for clean look
        position: 'relative',
        mb: 4
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
        }}></div>
        <Typography variant="h6" gutterBottom className="vintage-text" sx={{ 
          fontFamily: 'var(--font-decorative)',
          color: 'var(--color-sepia)',
          mt: 2
        }}>
          Description
        </Typography>
        <Typography variant="body1" sx={{ 
          fontFamily: 'var(--font-serif)',
          color: 'var(--color-ink)',
          lineHeight: 1.6
        }}>
          {crop.description || "No description available."}
        </Typography>
      </Paper>

      {/* Ornamental Divider */}
      <div className="section-divider"></div>

      {/* Seasonality */}
      <Paper sx={{ 
        p: 3,
        background: 'transparent', // Transparent to show parchment
        border: '2px solid var(--color-border-vintage)',
        borderRadius: 0, // Remove rounded edges
        boxShadow: 'none', // Remove shadows for clean look
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
        }}></div>
        <Typography variant="h5" gutterBottom className="vintage-text" sx={{ 
          fontFamily: 'var(--font-decorative)',
          color: 'var(--color-sepia)',
          mt: 2,
          textAlign: 'center'
        }}>
          Seasonality Guide
        </Typography>
        <Box mt={3}>
          <MonthBarChart ranges={ranges} />
        </Box>
      </Paper>
    </Box>
  );
};

export default CropDetail;
